import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	Box,
	Button,
	Group,
	Loader,
	MediaQuery,
	Pagination,
	Paper,
	Table,
	Text,
	Title,
} from '@mantine/core'
import { getOrgCheckIns } from '../services/actions/checkInActions'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import CheckInFilterBlock, {
	formatCheckInFilterValue,
	getCheckInCustomFields,
	matchesAdditionalCheckInFilters,
} from '../components/CheckInFilterBlock'

const ORG_CHECK_INS_RESULT_LIMIT = 10000
const CHECKIN_CSV_FIELDS = [
	'id',
	'experienceName',
	'experienceSlug',
	'groupId',
	'travelerName',
	'travelerEmail',
	'travelerPhone',
	'accountName',
	'scheduledCheckInDate',
	'stationName',
	'bookingNumber',
	'purchasedItems',
	'warnings',
	'checkInStatus',
]

const detectDevice = () => (window.innerWidth < 768 ? 'mobile' : 'desktop')
const CORE_CSV_FIELD_SET = new Set(CHECKIN_CSV_FIELDS)

const getCustomFieldExportKey = (key) =>
	CORE_CSV_FIELD_SET.has(key) ? `customFields.${key}` : key

const AdminCheckInsPage = () => {
	const { orgSlug } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const requestedSearch = typeof location.state?.checkInSearch === 'string'
		? location.state.checkInSearch
		: undefined

	const [checkIns, setCheckIns] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [searchTerm, setSearchTerm] = useState(requestedSearch ?? '')
	const [selectedExperience, setSelectedExperience] = useState('')
	const [checkInFilter, setCheckInFilter] = useState('all')
	const [selectedStations, setSelectedStations] = useState([])
	const [itemFilter, setItemFilter] = useState([])
	const [customFieldFilters, setCustomFieldFilters] = useState([])
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(50)

	const normalizedSearchTerm = searchTerm.trim()
	const hasSearchTerm = normalizedSearchTerm.length > 0

	useEffect(() => {
		if (requestedSearch !== undefined) setSearchTerm(requestedSearch)
	}, [location.key, requestedSearch])

	useEffect(() => {
		const size = detectDevice()
		setPageSize(size === 'mobile' ? 20 : 50)
	}, [])

	useEffect(() => {
		if (!orgSlug) return undefined

		if (!hasSearchTerm) {
			setCheckIns([])
			setError('')
			setLoading(false)
			return undefined
		}

		let isCurrent = true
		const timeoutId = window.setTimeout(async () => {
			setLoading(true)
			try {
				const results = await getOrgCheckIns(orgSlug, {
					search: normalizedSearchTerm,
					experience: selectedExperience,
					checkInStatus: checkInFilter,
				})

				if (!isCurrent) return
				setCheckIns(Array.isArray(results) ? results : [])
				setError('')
			} catch (err) {
				if (!isCurrent) return
				console.error('Failed to fetch organization check-ins:', err)
				setError('Failed to load organization check-ins')
			} finally {
				if (isCurrent) setLoading(false)
			}
		}, 300)

		return () => {
			isCurrent = false
			window.clearTimeout(timeoutId)
		}
	}, [
		orgSlug,
		hasSearchTerm,
		normalizedSearchTerm,
		selectedExperience,
		checkInFilter,
	])

	useEffect(() => {
		setPage(1)
	}, [
		searchTerm,
		selectedExperience,
		checkInFilter,
		selectedStations,
		itemFilter,
		customFieldFilters,
	])

	const formatDate = (value) => {
		if (!value) return ''
		return String(value).slice(0, 10)
	}

	const safeText = (value) =>
		value === null || value === undefined ? '' : String(value)

	const truncate = (value, maxLength = 25) => {
		const text = safeText(value)
		return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text
	}

	const formatCheckInStatus = (checkIn) =>
		checkIn.checkInStatus ? '✅ Yes' : '❌ No'

	const getExperienceLabel = (checkIn) =>
		checkIn.experienceName ||
		checkIn.experienceShortName ||
		checkIn.experienceSlug ||
		'Unknown Experience'

	const experienceOptions = useMemo(() => {
		const byKey = new Map()

		checkIns.forEach((checkIn) => {
			const key = checkIn.experienceSlug || checkIn.experienceId
			if (!key || byKey.has(key)) return
			byKey.set(key, {
				value: key,
				label: getExperienceLabel(checkIn),
			})
		})

		return Array.from(byKey.values()).sort((a, b) =>
			a.label.localeCompare(b.label),
		)
	}, [checkIns])

	const stationOptions = useMemo(() => {
		return Array.from(
			new Set(
				checkIns
					.map((checkIn) => safeText(checkIn.stationName).trim())
					.filter(Boolean),
			),
		)
			.sort((a, b) => a.localeCompare(b))
			.map((stationName) => ({ value: stationName, label: stationName }))
	}, [checkIns])

	const itemOptions = useMemo(() => {
		const byKey = new Map()

		checkIns.forEach((checkIn) => {
			if (!Array.isArray(checkIn.purchasedItems)) return
			checkIn.purchasedItems.forEach((item) => {
				const value = safeText(item?.id || item?.name).trim()
				if (!value || byKey.has(value)) return
				const label = safeText(item?.name || item?.description || value).trim()
				byKey.set(value, {
					value,
					label: label || value,
				})
			})
		})

		return Array.from(byKey.values()).sort((a, b) =>
			a.label.localeCompare(b.label),
		)
	}, [checkIns])

	useEffect(() => {
		const validStations = new Set(stationOptions.map((option) => option.value))
		setSelectedStations((currentStations) =>
			currentStations.filter((station) => validStations.has(station)),
		)
	}, [stationOptions])

	useEffect(() => {
		const validItems = new Set(itemOptions.map((option) => option.value))
		setItemFilter((currentItems) =>
			currentItems.filter((item) => validItems.has(item)),
		)
	}, [itemOptions])

	const filteredCheckIns = checkIns.filter((checkIn) => {
		const term = searchTerm.trim().toLowerCase()
		const purchasedItemText = Array.isArray(checkIn.purchasedItems)
			? checkIn.purchasedItems
					.map((item) =>
						[item?.name, item?.description].filter(Boolean).join(' '),
					)
					.join(' ')
			: ''
		const customFieldText = Object.values(getCheckInCustomFields(checkIn))
			.map(formatCheckInFilterValue)
			.join(' ')

		const searchFields = [
			checkIn.id,
			getExperienceLabel(checkIn),
			checkIn.experienceSlug,
			checkIn.travelerName,
			checkIn.travelerEmail,
			checkIn.travelerPhone,
			checkIn.accountName,
			checkIn.bookingNumber,
			checkIn.groupId,
			checkIn.stationName,
			purchasedItemText,
			customFieldText,
		]

		const matchesSearch =
			!term ||
			searchFields.some((value) => safeText(value).toLowerCase().includes(term))

		const matchesExperience =
			!selectedExperience ||
			checkIn.experienceSlug === selectedExperience ||
			checkIn.experienceId === selectedExperience

		const matchesCheckInStatus =
			checkInFilter === 'all' ||
			(checkInFilter === 'checkedIn' && checkIn.checkInStatus) ||
			(checkInFilter === 'notCheckedIn' && !checkIn.checkInStatus)

		const matchesStation =
			selectedStations.length === 0 ||
			selectedStations.some(
				(station) =>
					safeText(checkIn.stationName).toLowerCase() ===
					station.toLowerCase(),
			)

		const matchesPurchasedItems =
			itemFilter.length === 0 ||
			(Array.isArray(checkIn.purchasedItems) &&
				checkIn.purchasedItems.some((item) =>
					itemFilter.includes(safeText(item?.id || item?.name).trim()),
				))

		const matchesAdditionalFilters = matchesAdditionalCheckInFilters(
			checkIn,
			customFieldFilters,
		)

		return (
			matchesSearch &&
			matchesExperience &&
			matchesCheckInStatus &&
			matchesStation &&
			matchesPurchasedItems &&
			matchesAdditionalFilters
		)
	})

	const formatCsvRows = (source) => {
		const customFieldColumns = Array.from(
			new Set(
				source.flatMap((checkIn) =>
					Object.keys(getCheckInCustomFields(checkIn)),
				),
			),
		).sort((a, b) => a.localeCompare(b))

		return source.map((checkIn) => {
			const customFields = getCheckInCustomFields(checkIn)
			const exportedCustomFields = customFieldColumns.reduce((acc, key) => {
				acc[getCustomFieldExportKey(key)] = customFields[key] ?? ''
				return acc
			}, {})

			return {
				id: checkIn.id,
				experienceName: getExperienceLabel(checkIn),
				experienceSlug: checkIn.experienceSlug || '',
				groupId: checkIn.groupId || '',
				travelerName: checkIn.travelerName || '',
				travelerEmail: checkIn.travelerEmail || '',
				travelerPhone: checkIn.travelerPhone || '',
				accountName: checkIn.accountName || '',
				scheduledCheckInDate: checkIn.scheduledCheckInDate || '',
				stationName: checkIn.stationName || '',
				bookingNumber: checkIn.bookingNumber || '',
				purchasedItems: JSON.stringify(checkIn.purchasedItems || []),
				warnings: JSON.stringify(checkIn.warnings || []),
				checkInStatus: checkIn.checkInStatus ?? false,
				...exportedCustomFields,
			}
		})
	}

	const downloadCheckIns = (source, filename) => {
		const formatted = formatCsvRows(source)
		const csv =
			formatted.length > 0
				? Papa.unparse(formatted)
				: Papa.unparse({ fields: CHECKIN_CSV_FIELDS, data: [] })
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		saveAs(blob, filename)
	}

	const hasResultFilters =
		!!selectedExperience ||
		checkInFilter !== 'all' ||
		selectedStations.length > 0 ||
		itemFilter.length > 0 ||
		customFieldFilters.some(
			(filter) => filter.field || filter.values.length > 0,
		)

	const paginatedCheckIns = filteredCheckIns.slice(
		(page - 1) * pageSize,
		page * pageSize,
	)
	const totalPages = Math.ceil(filteredCheckIns.length / pageSize)
	const totalCheckInCount = checkIns.length
	const filteredCheckInCount = filteredCheckIns.length
	const hasReachedResultLimit = totalCheckInCount === ORG_CHECK_INS_RESULT_LIMIT
	const resultLimitLabel = ORG_CHECK_INS_RESULT_LIMIT.toLocaleString()
	const resultScopeLabel = hasReachedResultLimit
		? `first ${resultLimitLabel} matching check-ins`
		: `${totalCheckInCount} matching check-ins`
	const currentStart = filteredCheckInCount > 0 ? (page - 1) * pageSize + 1 : 0
	const currentEnd =
		filteredCheckInCount > 0
			? Math.min(page * pageSize, filteredCheckInCount)
			: 0

	const checkInCountLabel = !hasSearchTerm
		? 'Search to view check-ins'
		: filteredCheckInCount > 0
			? hasResultFilters || filteredCheckInCount !== totalCheckInCount
				? `Showing ${currentStart}-${currentEnd} of ${filteredCheckInCount} filtered check-ins (${resultScopeLabel})`
				: `Showing ${currentStart}-${currentEnd} of ${resultScopeLabel}`
			: hasResultFilters
				? `Showing 0 filtered check-ins (${resultScopeLabel})`
				: 'Showing 0 matching check-ins'

	const goToCheckIn = (checkIn) => {
		if (!checkIn.experienceSlug) return
		navigate(
			`/${orgSlug}/experiences/${checkIn.experienceSlug}/checkIn/${checkIn.id}`,
		)
	}

	const goToEditCheckIn = (checkIn) => {
		if (!checkIn.experienceSlug) return
		navigate(
			`/${orgSlug}/experiences/${checkIn.experienceSlug}/manage/checkin/${checkIn.id}`,
		)
	}

	const renderActions = (checkIn) => (
		<Group spacing='xs'>
			<Button
				size='xs'
				style={{ width: 85 }}
				disabled={!checkIn.experienceSlug}
				onClick={() => goToCheckIn(checkIn)}
			>
				{checkIn.checkInStatus ? 'View' : 'Check In'}
			</Button>
			<Button
				size='xs'
				disabled={!checkIn.experienceSlug}
				onClick={() => goToEditCheckIn(checkIn)}
			>
				Edit
			</Button>
		</Group>
	)

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Organization Check-Ins
			</Title>

			{error && <Text color='red'>{error}</Text>}

			<Group mb='sm'>
				<Button
					disabled={filteredCheckInCount === 0}
					onClick={() =>
						downloadCheckIns(
							filteredCheckIns,
							`${orgSlug}_checkins_results.csv`,
						)
					}
				>
					Download Results
				</Button>
			</Group>

			<CheckInFilterBlock
				checkIns={checkIns}
				searchTerm={searchTerm}
				onSearchTermChange={setSearchTerm}
				searchPlaceholder='Search name, email, phone, booking, experience...'
				checkInStatus={checkInFilter}
				onCheckInStatusChange={setCheckInFilter}
				stationFilter={selectedStations}
				onStationFilterChange={setSelectedStations}
				stationOptions={stationOptions}
				itemFilter={itemFilter}
				onItemFilterChange={setItemFilter}
				itemOptions={itemOptions}
				filters={customFieldFilters}
				onFiltersChange={setCustomFieldFilters}
				extraDedicatedFilters={[
					{
						key: 'experience',
						label: 'Experience',
						placeholder: 'All experiences',
						value: selectedExperience,
						onChange: (value) => setSelectedExperience(value || ''),
						data: [
							{ value: '', label: 'All experiences' },
							...experienceOptions,
						],
						searchable: true,
						clearable: true,
					},
				]}
			/>

			{hasSearchTerm && !loading && hasReachedResultLimit && (
				<Text size='xs' c='orange' mb='xs'>
					Results limited to {resultLimitLabel} rows
				</Text>
			)}

			{!hasSearchTerm ? (
				<Text c='dimmed'>{checkInCountLabel}</Text>
			) : loading ? (
				<Loader />
			) : paginatedCheckIns.length === 0 ? (
				<Text>No check-ins found</Text>
			) : (
				<>
					<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
						<Box>
							{paginatedCheckIns.map((checkIn) => (
								<Paper key={checkIn.id} withBorder p='sm' mb='sm'>
									<Text fw={600}>{checkIn.travelerName}</Text>
									<Text size='xs' c='dimmed'>
										{getExperienceLabel(checkIn)}
									</Text>
									<Text size='xs' c='dimmed'>
										{checkIn.travelerEmail}
									</Text>
									<Text size='xs' c='dimmed'>
										{checkIn.travelerPhone}
									</Text>
									<Text size='xs' c='dimmed'>
										{checkIn.accountName}
									</Text>
									<Text size='xs'>
										Date: {formatDate(checkIn.scheduledCheckInDate)}
									</Text>
									<Text size='xs'>Station: {checkIn.stationName}</Text>
									<Text size='xs'>Booking: {checkIn.bookingNumber}</Text>
									<Text size='xs'>
										Checked In?: {formatCheckInStatus(checkIn)}
									</Text>
									<Box mt='xs'>{renderActions(checkIn)}</Box>
								</Paper>
							))}
						</Box>
					</MediaQuery>

					<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
						<Box>
							<Paper withBorder p='sm'>
								<Table striped highlightOnHover>
									<thead>
										<tr>
											<th>Experience</th>
											<th>Booking</th>
											<th>Name</th>
											<th>Email</th>
											<th>Phone</th>
											<th>Checked In?</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody>
										{paginatedCheckIns.map((checkIn) => (
											<tr key={checkIn.id}>
												<td>{truncate(getExperienceLabel(checkIn), 28)}</td>
												<td>{checkIn.bookingNumber}</td>
												<td>{checkIn.travelerName}</td>
												<td>{truncate(checkIn.travelerEmail)}</td>
												<td>{truncate(checkIn.travelerPhone, 18)}</td>
												<td>{formatCheckInStatus(checkIn)}</td>
												<td>{renderActions(checkIn)}</td>
											</tr>
										))}
									</tbody>
								</Table>
							</Paper>
						</Box>
					</MediaQuery>
					<Box mt='sm'>
						<Text size='sm' c='dimmed' mb={totalPages > 1 ? 'xs' : 0}>
							{checkInCountLabel}
						</Text>
						{totalPages > 1 && (
							<Pagination
								value={page}
								onChange={setPage}
								total={totalPages}
								size='sm'
							/>
						)}
					</Box>
				</>
			)}
		</Box>
	)
}

export default AdminCheckInsPage
